import {
  Component,
  Injector,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatOptionModule } from '@angular/material/core'
import { MatSelectChange, MatSelectModule } from '@angular/material/select'
import { MatIcon } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { FormBuilder } from '@angular/forms'
import { Validators } from '@angular/forms'
import { Dialog } from '@angular/cdk/dialog'
import { ApiError } from '../../../types/apiError'
import { ErrorService } from '../../../services/error.service'
import { DialogService } from '../../../services/dialog.service'
import { SpinnerService } from '../../../services/spinner.service'
import { CompanyService } from '../../../services/company.service'
import { RouteService } from '../../../services/route.service'
import {
  CompanyForm,
  CompanyWithLocation,
  CountryLocation,
  StateLocation,
  CityLocation,
} from '../../../types/company'
import { Router } from '@angular/router'
import countryList from '../../../data/countries+states+cities.json'
import { ROUTES } from '../../../constants/routes'
import { TranslationService } from '../../../services/translation.service'
@Component({
  selector: 'app-company-create',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './create-company.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-company.component.scss',
})
export class CreateCompanyComponent implements OnInit {
  private injector = inject(Injector)
  private formBuilder = inject(FormBuilder)
  dialog = inject(Dialog)
  private errorService = inject(ErrorService)
  private dialogService = inject(DialogService)
  private spinnerService = inject(SpinnerService)
  private companyService = inject(CompanyService)
  private routeService = inject(RouteService)
  private router = inject(Router)
  translationService = inject(TranslationService)

  public countries: Array<CountryLocation> = []
  public states: Array<StateLocation> = []
  public cities: Array<CityLocation> = []

  companyForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    identification: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phone_number: ['', [Validators.required]],
    country: ['', [Validators.required]],
    state: ['', [Validators.required]],
    city: ['', [Validators.required]],
    global_location_number: ['', [Validators.required]],
    data_universal_numbering_system: ['', [Validators.required]],
    legal_entity_identifier: ['', [Validators.required]],
  })

  async ngOnInit(): Promise<void> {
    try {
      const currentRoute = this.routeService.getCompanyRoutes(
        ROUTES.CREATE_COMPANY.title,
        `admin/companies/${ROUTES.CREATE_COMPANY.path}`,
      )
      this.routeService.setRoute(currentRoute)
      this.spinnerService.startSpinner()
      let countryData: { data: [] } = { data: [] }
      countryData = Object.assign(countryData, countryList)

      this.countries = countryData.data.filter((country: CountryLocation) => {
        return country.states?.length > 0
      })
      this.spinnerService.stopSpinner()
    } catch {
      this.spinnerService.stopSpinner()
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getSomethingWentWrongText(),
        this.translationService.getErrorOccurredText(),
      )
    }
  }
  /**
   * Updates the list of states when the country is changed
   * @param {MatSelectChange} event Event containing new country
   */
  handleCountryChange(event: MatSelectChange) {
    const country = event.value
    this.states = [
      ...country.states.filter((state: StateLocation) => {
        return state.cities?.length > 0
      }),
    ]
  }

  /**
   * Updates the list of cities when the state is changed
   * @param {MatSelectChange} event Event containing new state
   */
  handleStateChange(event: MatSelectChange) {
    const state = event.value
    this.cities = [...state.cities]
  }

  /**
   * Submits form data to api
   */
  async onSubmit() {
    if (!this.companyForm.valid) {
      return
    }

    try {
      this.spinnerService.startSpinner()

      const companyData: CompanyWithLocation = Object.assign(
        {},
        this.companyForm.value,
      ) as CompanyWithLocation
      companyData.city = (companyData.city as CityLocation)?.name
      companyData.state = (companyData.state as StateLocation)?.name
      companyData.country = (companyData.country as CountryLocation)?.name

      await this.companyService.create(companyData as CompanyForm).toPromise()

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getCompaniesText(),
        this.translationService.getCompanyCreatedSuccessfullyText(),
      )

      this.router.navigate([`${ROUTES.ADMIN.path}/${ROUTES.COMPANY.path}`])
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form data
   */
  onReset() {
    this.companyForm.reset()
  }
}
